import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ShieldCheck,
  CheckCircle2,
  Clock,
  XCircle,
} from "lucide-react";

const statusBadge = (s) => {
  const map = {
    approved: { c: "bg-emerald-50 text-emerald-700 border-emerald-200", I: CheckCircle2 },
    pending: { c: "bg-amber-50 text-amber-700 border-amber-200", I: Clock },
    rejected: { c: "bg-red-50 text-red-700 border-red-200", I: XCircle },
  };
  const e = map[s] || map.pending;
  const I = e.I;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${e.c}`}
    >
      <I className="w-3.5 h-3.5" /> {s}
    </span>
  );
};

export default function RecruiterProfile({ me }) {
  if (!me) return null;

  const items = [
    { icon: Mail, label: "Email", value: me.email },
    { icon: Phone, label: "Phone", value: `${me.phone_code} ${me.phone}` },
    { icon: Building2, label: "Company", value: me.company_name },
    { icon: MapPin, label: "Location", value: `${me.city}, ${me.country}` },
    { icon: Calendar, label: "Experience", value: me.years_experience },
    {
      icon: Calendar,
      label: "Joined",
      value: new Date(me.created_at).toLocaleDateString(),
    },
  ];

  return (
    <div className="max-w-4xl space-y-5">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8"
      >
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#C49A3A]/12 blur-3xl" />
        <div className="relative flex flex-col sm:flex-row gap-5 items-start">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#3B5BC9] text-white flex items-center justify-center font-bold text-3xl shadow-md font-display">
            {me.full_name?.slice(0, 1)}
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-gold font-semibold">
              Partner Profile
            </div>
            <h2 className="mt-1 font-display text-3xl font-semibold text-slate-900">
              {me.full_name}
            </h2>
            <p className="text-slate-500">{me.company_name}</p>
            <div className="mt-3">{statusBadge(me.status)}</div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((it, idx) => (
          <motion.div
            key={it.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04 }}
            className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1E3A8A]/10 to-[#C49A3A]/10 flex items-center justify-center">
              <it.icon className="w-5 h-5 text-cct-blue" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                {it.label}
              </div>
              <div className="font-medium text-slate-800 truncate">
                {it.value}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {me.message && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5"
        >
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Application Notes
          </div>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            {me.message}
          </p>
        </motion.div>
      )}

      {me.documents?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-cct-blue" />
            <h3 className="font-display text-lg font-semibold text-slate-900">
              Verification Documents
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {me.documents.map((d) => (
              <div
                key={d.file_id}
                className="flex items-center gap-3 border border-slate-200 rounded-xl px-4 py-3"
              >
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-cct-blue" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate capitalize">
                    {d.label?.replace(/_/g, " ")}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {d.filename}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
